/**
 * Status Service - WebSocket abstraction for real-time status updates
 *
 * Current implementation: Mock data with simulated updates
 * Future implementation: Replace with actual WebSocket connection
 */

import type {
  IStatusService,
  StatusMessage,
  CctvStatus,
  ParkingLocationStatus,
  StatusData,
} from '../types/status';
import { log, LogCategory } from '../../../map-editor-shared/lib/logger'

/**
 * Mock Status Service
 * Simulates WebSocket connection with random status updates
 *
 * To replace with real WebSocket:
 * 1. Replace connect() to establish WebSocket connection
 * 2. Replace disconnect() to close WebSocket
 * 3. Replace updateInterval with WebSocket message handlers
 * 4. Keep the same IStatusService interface
 */
class MockStatusService implements IStatusService {
  private connected = false;
  private subscribers: Set<(message: StatusMessage) => void> = new Set();
  private updateInterval: ReturnType<typeof setInterval> | null = null;

  // Mock data: Object IDs grouped by floor for simulation
  private mockCctvIdsByFloor: Map<string, string[]> = new Map();
  private mockParkingIdsByFloor: Map<string, string[]> = new Map();

  // Persistent state for parking (80% fixed, 10% changeable)
  private parkingState: Map<string, { occupied: boolean; isChangeable: boolean }> = new Map();

  /**
   * Initialize with object IDs grouped by floor for mock data
   * In real implementation, these would come from the server
   */
  initialize(objectsByFloor: { floorId: string; cctvIds: string[]; parkingIds: string[] }[]) {
    this.mockCctvIdsByFloor.clear();
    this.mockParkingIdsByFloor.clear();

    objectsByFloor.forEach(({ floorId, cctvIds, parkingIds }) => {
      this.mockCctvIdsByFloor.set(floorId, cctvIds);
      this.mockParkingIdsByFloor.set(floorId, parkingIds);
    });

    log.info(LogCategory.API, 'StatusService Initializing with objects by floor:', {
      floors: objectsByFloor.map(f => ({
        floorId: f.floorId,
        cctvCount: f.cctvIds.length,
        parkingCount: f.parkingIds.length,
      })),
    });
  }

  connect(): void {
    if (this.connected) return;

    this.connected = true;
    log.info(LogCategory.API, 'StatusService Connected (mock)');

    // Send initial bulk update
    this.sendBulkUpdate();

    // Simulate random status updates every 15 seconds
    this.updateInterval = setInterval(() => {
      this.sendRandomUpdate();
    }, 15000); // 15 seconds
  }

  disconnect(): void {
    if (!this.connected) return;

    this.connected = false;
    log.info(LogCategory.API, 'StatusService Disconnected (mock)');

    if (this.updateInterval) {
      clearInterval(this.updateInterval);
      this.updateInterval = null;
    }
  }

  subscribe(callback: (message: StatusMessage) => void): () => void {
    this.subscribers.add(callback);

    // Return unsubscribe function
    return () => {
      this.subscribers.delete(callback);
    };
  }

  isConnected(): boolean {
    return this.connected;
  }

  /**
   * Send bulk initial status update with floor-specific variations
   * Scalable to any number of floors with diverse mock data patterns
   */
  private sendBulkUpdate(): void {
    const cctvStatuses: Record<string, CctvStatus> = {};
    const parkingStatuses: Record<string, ParkingLocationStatus> = {};

    let floorIndex = 0;

    // Process each floor with different occupancy rates
    this.mockParkingIdsByFloor.forEach((parkingIds, floorId) => {
      // Generate unique occupancy rate for each floor using hash-like function
      // This ensures consistent but varied rates regardless of floor count
      const floorHash = this.hashString(floorId);
      const baseOccupancyRate = 0.2 + (floorHash % 70) / 100; // 20% to 90%

      const totalParkingSpots = parkingIds.length;
      const occupiedFixedCount = Math.floor(totalParkingSpots * baseOccupancyRate * 0.85); // 85% of occupied is fixed
      const emptyFixedCount = Math.floor(totalParkingSpots * (1 - baseOccupancyRate) * 0.85); // 85% of empty is fixed

      // Shuffle parking IDs to randomly assign states (seeded by floor for consistency)
      const shuffledIds = [...parkingIds].sort((a, b) => {
        const hashA = this.hashString(a + floorId);
        const hashB = this.hashString(b + floorId);
        return hashA - hashB;
      });

      shuffledIds.forEach((id, index) => {
        let occupied: boolean;
        let isChangeable: boolean;

        if (index < occupiedFixedCount) {
          // Fixed occupied
          occupied = true;
          isChangeable = false;
        } else if (index < occupiedFixedCount + emptyFixedCount) {
          // Fixed empty
          occupied = false;
          isChangeable = false;
        } else {
          // Changeable (seeded random for initial state)
          occupied = this.hashString(id) % 2 === 0;
          isChangeable = true;
        }

        // Store persistent state
        this.parkingState.set(id, { occupied, isChangeable });

        parkingStatuses[id] = {
          objectId: id,
          occupied,
          lastUpdate: Date.now(),
          vehicleInfo: occupied ? {
            plateNumber: this.generatePlateNumber(),
            entryTime: Date.now() - Math.random() * 3600000, // Up to 1 hour ago
          } : undefined,
        };
      });

      const actualOccupied = occupiedFixedCount + shuffledIds.slice(occupiedFixedCount + emptyFixedCount).filter((id) => parkingStatuses[id].occupied).length;

      log.debug(LogCategory.API, `StatusService Floor ${floorId.substring(0, 8)}... parking state:`);
      log.debug(LogCategory.API, `Occupancy Rate: ${Math.round(baseOccupancyRate * 100)}%`);
      log.debug(LogCategory.API, `Total Spots: ${totalParkingSpots}`);
      log.debug(LogCategory.API, `Occupied: ${actualOccupied} (${Math.round((actualOccupied / totalParkingSpots) * 100)}%)`);
      log.debug(LogCategory.API, `Empty: ${totalParkingSpots - actualOccupied}`);

      floorIndex++;
    });

    // Initialize all CCTVs with floor-specific connection rates
    floorIndex = 0;
    this.mockCctvIdsByFloor.forEach((cctvIds, floorId) => {
      // Generate unique CCTV connection rate for each floor (98% to 100%)
      const floorHash = this.hashString(floorId + 'cctv');
      const connectionRate = 0.98 + (floorHash % 2) / 100; // 98% to 100%

      cctvIds.forEach((id) => {
        // Use hash-based deterministic "randomness" for consistent results
        const idHash = this.hashString(id + floorId);
        const isConnected = (idHash % 100) / 100 < connectionRate;

        cctvStatuses[id] = {
          objectId: id,
          connected: isConnected,
          lastUpdate: Date.now(),
          errorMessage: isConnected ? undefined : 'Network timeout - 연결 끊김',
        };
      });

      const connectedCount = cctvIds.filter(id => cctvStatuses[id].connected).length;

      log.debug(LogCategory.API, `StatusService Floor ${floorId.substring(0, 8)}... CCTV state:`);
      log.debug(LogCategory.API, `Connection Rate: ${Math.round(connectionRate * 100)}%`);
      log.debug(LogCategory.API, `Total CCTVs: ${cctvIds.length}`);
      log.debug(LogCategory.API, `Connected: ${connectedCount}`);
      log.debug(LogCategory.API, `Disconnected: ${cctvIds.length - connectedCount}`);

      floorIndex++;
    });

    const statusData: StatusData = { cctvStatuses, parkingStatuses, lightingStatuses: {} };
    const message: StatusMessage = {
      type: 'bulk_update',
      data: statusData,
    };

    this.broadcast(message);
  }

  /**
   * Simple hash function for generating consistent pseudo-random values
   * Used to ensure each floor gets different but reproducible mock data
   */
  private hashString(str: string): number {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash);
  }

  /**
   * Send random status update every 15 seconds
   * - Updates only changeable parking spots (10%)
   * - Randomly selects 1 CCTV with 90% connected / 10% disconnected probability
   */
  private sendRandomUpdate(): void {
    // Update only changeable parking spots (10%)
    this.updateChangeableParkingStatuses();

    // Update 1 random CCTV with 90/10 probability
    this.updateRandomCctvStatus();
  }

  /**
   * Update only changeable parking spots (10% of total)
   */
  private updateChangeableParkingStatuses(): void {
    if (this.parkingState.size === 0) return;

    const parkingStatuses: Record<string, ParkingLocationStatus> = {};

    // Only update changeable parking spots
    this.parkingState.forEach((state, id) => {
      if (state.isChangeable) {
        // Randomly toggle occupied status
        const occupied = Math.random() < 0.5;

        // Update persistent state
        this.parkingState.set(id, { ...state, occupied });

        parkingStatuses[id] = {
          objectId: id,
          occupied,
          lastUpdate: Date.now(),
          vehicleInfo: occupied ? {
            plateNumber: this.generatePlateNumber(),
            entryTime: Date.now() - Math.random() * 3600000, // Up to 1 hour ago
          } : undefined,
        };
      }
    });

    if (Object.keys(parkingStatuses).length === 0) return;

    const statusData: StatusData = { cctvStatuses: {}, parkingStatuses, lightingStatuses: {} };
    const message: StatusMessage = {
      type: 'bulk_update',
      data: statusData,
    };

    this.broadcast(message);
  }

  /**
   * Update 1 random CCTV per floor with 99% connected / 1% disconnected probability
   */
  private updateRandomCctvStatus(): void {
    if (this.mockCctvIdsByFloor.size === 0) return;

    const cctvStatuses: Record<string, CctvStatus> = {};

    // Update one CCTV from each floor
    this.mockCctvIdsByFloor.forEach((cctvIds) => {
      if (cctvIds.length === 0) return;

      // Randomly select 1 CCTV from this floor
      const randomIndex = Math.floor(Math.random() * cctvIds.length);
      const randomCctvId = cctvIds[randomIndex];

      // 99% connected, 1% disconnected (very rare disconnection)
      const isConnected = Math.random() < 0.99;

      cctvStatuses[randomCctvId] = {
        objectId: randomCctvId,
        connected: isConnected,
        lastUpdate: Date.now(),
        errorMessage: isConnected ? undefined : 'Network timeout - 연결 끊김',
      };
    });

    const statusData: StatusData = { cctvStatuses, parkingStatuses: {}, lightingStatuses: {} };
    const message: StatusMessage = {
      type: 'bulk_update',
      data: statusData,
    };

    this.broadcast(message);
  }


  /**
   * Broadcast message to all subscribers
   */
  private broadcast(message: StatusMessage): void {
    this.subscribers.forEach(callback => callback(message));
  }

  /**
   * Generate random Korean plate number
   */
  private generatePlateNumber(): string {
    const regions = ['서울', '경기', '인천', '부산', '대구'];
    const region = regions[Math.floor(Math.random() * regions.length)];
    const numbers = Math.floor(Math.random() * 9000) + 1000;
    return `${region} ${numbers}`;
  }
}

/**
 * Real WebSocket Service (placeholder for future implementation)
 *
 * Uncomment and implement when WebSocket server is ready:
 */
/*
class RealStatusService implements IStatusService {
  private ws: WebSocket | null = null;
  private subscribers: Set<(message: StatusMessage) => void> = new Set();

  connect(): void {
    this.ws = new WebSocket(process.env.REACT_APP_WS_URL);
import { log, LogCategory } from '../../../map-editor-shared/lib/logger'

    this.ws.onopen = () => {
      log.info(LogCategory.API, 'StatusService WebSocket connected');
    };

    this.ws.onmessage = (event) => {
      const message: StatusMessage = JSON.parse(event.data);
      this.broadcast(message);
    };

    this.ws.onerror = (error) => {
      log.error(LogCategory.API, 'StatusService WebSocket error:', error);
    };

    this.ws.onclose = () => {
      log.info(LogCategory.API, 'StatusService WebSocket disconnected');
    };
  }

  disconnect(): void {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }

  subscribe(callback: (message: StatusMessage) => void): () => void {
    this.subscribers.add(callback);
    return () => {
      this.subscribers.delete(callback);
    };
  }

  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  private broadcast(message: StatusMessage): void {
    this.subscribers.forEach(callback => callback(message));
  }
}
*/

// Export singleton instance
// To switch to real WebSocket: change MockStatusService to RealStatusService
export const statusService = new MockStatusService();
